import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Calendar, CalendarDays, FileText, Users, Award, Clock, CheckCircle2, XCircle, Download, BookOpen,
  Receipt, Bell, Target, Plus, X, LayoutGrid, Building2, AlertCircle, ChevronRight, Briefcase, Mail,
  Banknote, Phone, MapPin, GraduationCap, Shield, KeyRound, Eye, EyeOff, Save, Pencil, Search,
  UserMinus, AlertTriangle, Upload, UserCog, ArrowRight, Check,
} from 'lucide-react';
import http from '../api/http';
import { useAuth } from '../context/AuthContext';
import { useFiliale } from '../context/FilialeContext';

/* ═════════════════════════ Identité visuelle (Talentia) ═════════════════════════ */
const C = {
  navy: '#0B1437', navy2: '#18246B', y: '#FFC93C', teal: '#25B8A8', coral: '#FF7A6B',
  vio: '#8B7CF6', sky: '#4EA8FF', mut: '#667091', line: '#E2E6F2', bg: '#F1F3FA',
};
const PALETTE = [C.sky, C.teal, C.y, C.vio, C.coral];

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@500;700;800&family=DM+Sans:wght@400;500;700&display=swap');
.ee { --navy:${C.navy}; --navy2:${C.navy2}; --y:${C.y}; --teal:${C.teal}; --coral:${C.coral}; --vio:${C.vio}; --sky:${C.sky}; --mut:${C.mut}; --line:${C.line}; --bg:${C.bg};
  background: var(--bg); color: var(--navy); font-family: 'DM Sans', system-ui, -apple-system, 'Segoe UI', sans-serif; min-height: 100vh; }
.ee-display { font-family: 'Bricolage Grotesque', 'DM Sans', system-ui, sans-serif; letter-spacing: -.02em; }
.ee-muted { color: var(--mut); }
.ee-hero { background: linear-gradient(120deg, var(--navy), var(--navy2) 72%, #22318F); }
.ee-orb { position: absolute; border-radius: 9999px; pointer-events: none; }
.ee-avatar-ring { padding: 3px; border-radius: 28px; background: conic-gradient(from 160deg, var(--y), var(--teal), var(--vio), var(--sky), var(--y)); }
.ee-glass { background: rgba(255,255,255,.07); border: 1px solid rgba(255,255,255,.12); border-radius: 16px; transition: background .2s, transform .2s; }
button.ee-glass:hover { background: rgba(255,255,255,.13); transform: translateY(-2px); }
.ee-card { background: #fff; border: 1px solid var(--line); border-radius: 20px; box-shadow: 0 1px 2px rgba(11,20,55,.04); transition: box-shadow .25s; }
.ee-card:hover { box-shadow: 0 14px 32px -18px rgba(11,20,55,.28); }
.ee-ico { display: flex; align-items: center; justify-content: center; flex: none; width: 32px; height: 32px; border-radius: 10px; }
.ee-tabs { background: rgba(255,255,255,.93); backdrop-filter: blur(10px); border: 1px solid var(--line); border-radius: 18px; box-shadow: 0 12px 30px -14px rgba(11,20,55,.4); }
.ee-tab { display: inline-flex; align-items: center; gap: 8px; padding: 10px 16px; border-radius: 13px; font-size: 14px; font-weight: 700; color: var(--mut); white-space: nowrap; transition: background .2s, color .2s; }
.ee-tab:hover { color: var(--navy); background: var(--bg); }
.ee-tab[aria-current="page"] { background: var(--navy); color: #fff; }
.ee-tab[aria-current="page"] svg { color: var(--y); }
.ee-badge { display: inline-flex; align-items: center; justify-content: center; min-width: 18px; height: 18px; padding: 0 5px; border-radius: 9px; background: var(--coral); color: var(--navy); font-size: 11px; font-weight: 800; }
.ee-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 10px 16px; border-radius: 12px; font-size: 14px; font-weight: 700; cursor: pointer; transition: transform .15s, background .15s, box-shadow .15s; }
.ee-btn:hover:not(:disabled) { transform: translateY(-1px); }
.ee-btn:disabled { opacity: .5; cursor: not-allowed; }
.ee-btn-navy { background: var(--navy); color: #fff; }
.ee-btn-navy:hover:not(:disabled) { background: var(--navy2); }
.ee-btn-yellow { background: var(--y); color: var(--navy); }
.ee-btn-teal { background: var(--teal); color: var(--navy); }
.ee-btn-glass { background: rgba(255,255,255,.12); color: #fff; border: 1px solid rgba(255,255,255,.18); }
.ee-btn-ghost { background: #fff; color: var(--navy); border: 1px solid var(--line); }
.ee-btn-ghost:hover:not(:disabled) { background: var(--bg); }
.ee-btn-sm { padding: 6px 12px; font-size: 12px; border-radius: 10px; }
.ee-input { width: 100%; padding: 10px 12px; font-size: 14px; color: var(--navy); background: #fff; border: 1px solid var(--line); border-radius: 12px; transition: border-color .15s, box-shadow .15s; }
.ee-input:focus { outline: none; border-color: var(--navy2); box-shadow: 0 0 0 3px rgba(24,36,107,.12); }
.ee-input:disabled { background: var(--bg); }
.ee-focus:focus-visible, .ee-btn:focus-visible, .ee-tab:focus-visible { outline: 3px solid var(--y); outline-offset: 2px; }
.ee-check { appearance: none; display: grid; place-content: center; flex: none; width: 20px; height: 20px; border: 2px solid #C9D0E6; border-radius: 7px; cursor: pointer; transition: background .15s, border-color .15s; }
.ee-check:checked { background: var(--teal); border-color: var(--teal); }
.ee-check:checked::after { content: ''; width: 9px; height: 5px; border-left: 2.5px solid var(--navy); border-bottom: 2.5px solid var(--navy); transform: rotate(-45deg) translate(1px,-1px); }
.ee-day { display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 12px 6px; text-align: center; border: 1px solid var(--line); border-radius: 16px; background: #fff; transition: transform .2s; }
.ee-day:hover { transform: translateY(-2px); }
.ee-day-on { background: #EAF3FF; border-color: #CFE3FF; color: #1F5FB4; }
.ee-day-off { background: var(--bg); color: var(--mut); }
.ee-day-ferie { background: #FFE9E6; border-color: #FFD3CC; color: #9A2B1E; }
.ee-day-today { background: var(--navy); border-color: var(--navy); color: #fff; box-shadow: 0 10px 22px -10px rgba(11,20,55,.6); }
.ee-ticket { display: flex; align-items: stretch; border: 1px solid var(--line); border-left: 5px solid var(--teal); border-radius: 16px; background: linear-gradient(90deg, #F4FBFA, #fff 40%); }
.ee-ticket.is-off { border-left-color: var(--coral); background: linear-gradient(90deg, #FFF5F3, #fff 40%); }
.ee-cut { margin: 10px 0; border-left: 2px dashed var(--line); }
.ee-slip { padding: 18px; border: 1px solid var(--line); border-radius: 18px; background: #fff; transition: transform .2s, box-shadow .2s, border-color .2s; }
.ee-slip:hover { transform: translateY(-3px); border-color: #C9D0E6; box-shadow: 0 16px 30px -20px rgba(11,20,55,.4); }
.ee-row { transition: background .15s; }
.ee-row:hover { background: #F7F8FD; }
@keyframes ee-up { from { opacity: 0; transform: translateY(12px); } }
.ee-in { animation: ee-up .5s cubic-bezier(.2,.7,.2,1) backwards; }
@keyframes ee-arc { from { stroke-dasharray: 0 400; } }
.ee-arc { animation: ee-arc 1.1s .2s cubic-bezier(.2,.7,.2,1) backwards; }
@keyframes ee-spin { to { transform: rotate(360deg); } }
.ee-spin { animation: ee-spin .8s linear infinite; }
@media (prefers-reduced-motion: reduce) { .ee-in, .ee-arc, .ee-spin { animation: none !important; } .ee-day, .ee-slip, .ee-btn, .ee-glass { transition: none; } }
`;

const cx = (...a) => a.filter(Boolean).join(' ');

/* ═════════════════════════ Briques d'interface (hors du composant principal) ═════════════════════════ */
const TONES = {
  amber: ['#FFF4D6', '#8A6200'], blue: ['#E6F1FF', '#1F5FB4'], violet: ['#EEEBFE', '#5B4BD0'],
  rose: ['#FFE9E6', '#9A2B1E'], green: ['#E3F7F4', '#0E7C70'], slate: ['#EEF0F7', '#556080'],
};
const Chip = ({ tone = 'slate', icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold rounded-full whitespace-nowrap" style={{ background: TONES[tone][0], color: TONES[tone][1] }}>
    {Icon && <Icon className="w-3 h-3" />}{children}
  </span>
);

const Card = ({ title, icon: Icon, accent = C.sky, action, flush, className = '', delay = 0, children }) => (
  <section className={cx('ee-card ee-in', className)} style={{ animationDelay: `${delay}ms` }}>
    <header className="flex items-center justify-between gap-3 px-5 pt-4 pb-3">
      <div className="flex items-center min-w-0 gap-2.5">
        {Icon && <span className="ee-ico" style={{ background: `${accent}2E`, color: C.navy }}><Icon className="w-4 h-4" /></span>}
        <h2 className="truncate ee-display text-[15px] font-bold">{title}</h2>
      </div>
      {action}
    </header>
    <div className={flush ? '' : 'px-5 pb-5'}>{children}</div>
  </section>
);

const Empty = ({ icon: Icon = FileText, title, hint, action }) => (
  <div className="flex flex-col items-center gap-2 px-4 py-9 text-center">
    <div className="flex items-end gap-1" aria-hidden="true">
      {[C.sky, C.teal, C.y].map((c, i) => <span key={c} className="rounded-full" style={{ width: 8 + i * 3, height: 8 + i * 3, background: c, opacity: .85 }} />)}
    </div>
    <div className="flex items-center justify-center w-11 h-11 -mt-1 rounded-2xl" style={{ background: C.bg }}><Icon className="w-5 h-5 ee-muted" /></div>
    <p className="text-sm font-bold">{title}</p>
    {hint && <p className="max-w-xs text-xs ee-muted">{hint}</p>}
    {action}
  </div>
);

const Loader = ({ label = 'Chargement' }) => (
  <div className="flex items-center justify-center gap-2 py-9 text-sm ee-muted" role="status">
    <span className="w-4 h-4 rounded-full ee-spin" style={{ border: `2px solid ${C.line}`, borderTopColor: C.navy2 }} />{label}
  </div>
);

const Btn = ({ tone = 'navy', size, icon: Icon, children, className = '', ...p }) => (
  <button type="button" className={cx('ee-btn', `ee-btn-${tone}`, size === 'sm' && 'ee-btn-sm', className)} {...p}>
    {Icon && <Icon className={size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'} />}{children}
  </button>
);

const Avatar = ({ src, nom, prenom, size = 40, radius = 14, className = '' }) => {
  const [bad, setBad] = useState(false);
  useEffect(() => setBad(false), [src]);
  const ini = `${(prenom || '')[0] || ''}${(nom || '')[0] || ''}`.toUpperCase() || '?';
  const color = PALETTE[((nom || '').charCodeAt(0) || 0) % PALETTE.length];
  return (
    <span className={cx('flex items-center justify-center flex-none overflow-hidden font-extrabold ee-display', className)}
      style={{ width: size, height: size, borderRadius: radius, background: color, color: C.navy, fontSize: size * 0.36 }}>
      {src && !bad ? <img src={src} alt="" className="object-cover w-full h-full" onError={() => setBad(true)} /> : ini}
    </span>
  );
};

const Ring = ({ pct, size = 124, stroke = 11, color = C.y, track = 'rgba(255,255,255,.16)', children }) => {
  const r = 50 - stroke / 2, len = 2 * Math.PI * r, p = Math.max(0, Math.min(100, pct));
  return (
    <div className="relative flex-none" style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" width={size} height={size} aria-hidden="true">
        <circle cx="50" cy="50" r={r} fill="none" stroke={track} strokeWidth={stroke} />
        <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" className="ee-arc"
          strokeDasharray={`${(len * p) / 100} ${len}`} transform="rotate(-90 50 50)" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">{children}</div>
    </div>
  );
};

const GoalBar = ({ label, value = 0, tone }) => {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const color = tone || (v >= 75 ? C.teal : v >= 40 ? C.sky : C.coral);
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <span className="text-sm truncate">{label}</span>
        <span className="text-xs font-bold tabular-nums ee-muted">{v}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full" style={{ background: C.bg }}>
        <div className="h-full rounded-full" style={{ width: `${v}%`, background: color, transition: 'width .9s cubic-bezier(.2,.7,.2,1)' }} />
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block mb-1.5 text-xs font-bold ee-muted">{label}</span>
    {children}
  </label>
);

const DateBlock = ({ value, tone = C.sky }) => {
  const d = value ? new Date(value) : null;
  const ok = d && !isNaN(d.getTime());
  return (
    <div className="flex flex-col items-center justify-center flex-none w-12 h-14 rounded-2xl" style={{ background: `${tone}26` }}>
      <span className="ee-display text-lg font-extrabold leading-none">{ok ? d.getDate() : '–'}</span>
      <span className="text-[10px] font-bold uppercase ee-muted">{ok ? d.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '') : ''}</span>
    </div>
  );
};

const FOCUSABLE = 'button:not([disabled]),input:not([disabled]),select,textarea,a[href],[tabindex]:not([tabindex="-1"])';
const Modal = ({ title, onClose, children, footer, wide }) => {
  const ref = useRef(null);
  useEffect(() => {
    const prev = document.activeElement, ov = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    ref.current?.querySelector('input,select,textarea')?.focus();
    return () => { document.body.style.overflow = ov; prev?.focus?.(); };
  }, []);
  useEffect(() => {
    const k = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Tab' && ref.current) {
        const f = [...ref.current.querySelectorAll(FOCUSABLE)]; if (!f.length) return;
        const a = document.activeElement;
        if (e.shiftKey && a === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && a === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      }
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  return createPortal(
    <div className="ee fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center" style={{ background: 'rgba(11,20,55,.55)', minHeight: 0 }}
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} role="dialog" aria-modal="true" aria-label={title} className="flex flex-col w-full bg-white shadow-2xl ee-in"
        style={{ maxWidth: wide ? 672 : 512, maxHeight: '92vh', borderRadius: 24, color: C.navy }}>
        <div className="flex items-center px-6 pt-5 pb-3">
          <h3 className="text-xl font-extrabold ee-display">{title}</h3>
          <button type="button" onClick={onClose} aria-label="Fermer" className="p-1.5 ml-auto rounded-lg ee-muted ee-focus"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-2 overflow-y-auto">{children}</div>
        {footer && <div className="flex justify-end gap-2 px-6 pt-3 pb-5 mt-1" style={{ borderTop: `1px solid ${C.line}` }}>{footer}</div>}
      </div>
    </div>, document.body);
};

/* ═════════════════════════ Constantes & utilitaires ═════════════════════════ */
const TABS = [
  { id: 'apercu', label: 'Aperçu', icon: LayoutGrid },
  { id: 'conges', label: 'Congés', icon: CalendarDays },
  { id: 'absences', label: 'Absences', icon: Clock },
  { id: 'paie', label: 'Paie', icon: Banknote },
  { id: 'carriere', label: 'Carrière', icon: Award },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'profil', label: 'Profil', icon: UserCog },
  { id: 'carnet', label: 'Carnet', icon: Users },
];

// Types de contrat pour lesquels l'onglet Paie est désactivé (IDs)
const CONTRATS_SANS_PAIE_IDS = [1, 4, 5]; // STAGE, FORMATION, PRESTATAIRE
const CONGE = { 0: ['amber', 'En attente', Clock], 1: ['green', 'Validé', CheckCircle2], 2: ['violet', 'Pré-validé', CheckCircle2], 3: ['rose', 'Rejeté', XCircle] };
const ABS = {
  en_attente_justification: ['amber', 'À justifier', AlertCircle], en_attente_validation: ['blue', 'En validation', Clock],
  justifiee: ['green', 'Justifiée', CheckCircle2], non_justifiee: ['rose', 'Non justifiée', XCircle], enregistree: ['slate', 'Enregistrée', CheckCircle2],
};
const MOB = { accepte: ['green', 'Acceptée'], refuse: ['rose', 'Refusée'], en_attente: ['amber', 'En attente'] };
const EMPTY_CERT = { type_certificat_id: '', numero: '', date_consultation: '', date_debut: '', date_fin: '', total_jour_repos: '' };
const EMPTY_MOB = { filiale_destination_id: '', titre_destination_id: '', motivation: '', disponibilite: '' };

const fmtDate = (v) => { if (!v) return '—'; const d = new Date(v); return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR'); };
const localISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const money = (v, devise = '') => (v == null || v === '' || isNaN(Number(v)) ? '—' : `${Number(v).toLocaleString('fr-FR')} ${devise}`.trim());
const MASK = '•••• •••';

const CongeChip = ({ etat }) => { const c = CONGE[etat]; return c ? <Chip tone={c[0]} icon={c[2]}>{c[1]}</Chip> : <Chip>{String(etat)}</Chip>; };
const pwdLevel = (p) => {
  const score = [p.length >= 8, /\d/.test(p), /[^a-zA-Z0-9]/.test(p), /[A-Z]/.test(p)].filter(Boolean).length;
  return [{ l: 'Trop court', c: C.coral, w: 25 }, { l: 'Faible', c: C.y, w: 50 }, { l: 'Correct', c: C.sky, w: 75 }, { l: 'Fort', c: C.teal, w: 100 }][Math.max(0, Math.min(score - 1, 3))];
};

// Télécharge un fichier binaire renvoyé par l'API
const downloadBlob = async (url, filename, config = {}) => {
  const response = await http.get(url, { responseType: 'blob', ...config });
  const href = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = href; link.setAttribute('download', filename);
  document.body.appendChild(link); link.click(); link.remove();
  window.URL.revokeObjectURL(href);
};

/* ═════════════════════════ Page ═════════════════════════ */
const EspaceEmploye = () => {
  const { user, logout } = useAuth();
  const { currentFiliale } = useFiliale();

  const [personnel, setPersonnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('apercu');
  const [notice, setNotice] = useState(null); // { type: 'success' | 'error', message }
  const noticeTimer = useRef(null);

  // Données
  const [conges, setConges] = useState(null);
  const [planning, setPlanning] = useState([]);
  const [joursFeries, setJoursFeries] = useState([]);
  const [taches, setTaches] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [sanctions, setSanctions] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [formations, setFormations] = useState([]);
  const [frais, setFrais] = useState([]);
  const [equipe, setEquipe] = useState([]);
  const [manager, setManager] = useState(null);
  const [actualites, setActualites] = useState([]);
  const [objectifs, setObjectifs] = useState([]);
  const [typesConge, setTypesConge] = useState([]);
  const [titresConge, setTitresConge] = useState([]);
  const [loadingTitres, setLoadingTitres] = useState(false);
  const [mesConges, setMesConges] = useState([]);
  const [loadingMesConges, setLoadingMesConges] = useState(false);
  const [bulletins, setBulletins] = useState([]);
  const [loadingBulletins, setLoadingBulletins] = useState(false);

  // Demande de congé
  const [showCongeModal, setShowCongeModal] = useState(false);
  const [congeForm, setCongeForm] = useState({ date_debut: '', date_fin: '', type_conge_id: 1, motif: '' });
  const [submittingConge, setSubmittingConge] = useState(false);

  // Mobilité
  const [showMobiliteModal, setShowMobiliteModal] = useState(false);
  const [mobiliteForm, setMobiliteForm] = useState(EMPTY_MOB);
  const [submittingMobilite, setSubmittingMobilite] = useState(false);
  const [mesDemandesMobilite, setMesDemandesMobilite] = useState([]);
  const [filiales, setFiliales] = useState([]);
  const [titres, setTitres] = useState([]);

  // Validation manager
  const [showValidation, setShowValidation] = useState(true);
  const [pendingConges, setPendingConges] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);

  // Absences
  const [showJustifierModal, setShowJustifierModal] = useState(false);
  const [selectedAbsence, setSelectedAbsence] = useState(null);
  const [certificatForm, setCertificatForm] = useState(EMPTY_CERT);
  const [certificatFile, setCertificatFile] = useState(null);
  const [submittingCert, setSubmittingCert] = useState(false);
  const [typeCertificatList, setTypeCertificatList] = useState([]);
  const [typeAbsenceList, setTypeAbsenceList] = useState([]);
  const [absenceStatutFilter, setAbsenceStatutFilter] = useState('');
  const [absenceTypeFilter, setAbsenceTypeFilter] = useState('');

  // Carnet d'adresses
  const [carnetContacts, setCarnetContacts] = useState([]);
  const [loadingCarnet, setLoadingCarnet] = useState(false);
  const [rechercheQuery, setRechercheQuery] = useState('');
  const [rechercheResultats, setRechercheResultats] = useState([]);
  const [loadingRecherche, setLoadingRecherche] = useState(false);
  const [filtreContacts, setFiltreContacts] = useState('');
  const [contactsPage, setContactsPage] = useState(1);
  const contactsPerPage = 8;

  // Paie
  const [paieFiltre, setPaieFiltre] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const [showAmounts, setShowAmounts] = useState(false); // montants masqués par défaut (confidentialité)

  // Profil
  const [profilForm, setProfilForm] = useState({ telephone: '', adresse: '' });
  const [accountForm, setAccountForm] = useState({ username: '', email: '' });
  const [pwdForm, setPwdForm] = useState({ ancien: '', nouveau: '', confirmer: '' });
  const [showPwd, setShowPwd] = useState({ ancien: false, nouveau: false, confirmer: false });
  const [savingProfil, setSavingProfil] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [profilEditing, setProfilEditing] = useState(false);
  const [accountEditing, setAccountEditing] = useState(false);

  const isManager = Boolean(user && (user.role === 'admin_pays' || user.role === 'manager'));

  const notify = (type, message) => {
    setNotice({ type, message });
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 4500);
  };
  useEffect(() => () => clearTimeout(noticeTimer.current), []);

  /* ───────────── Chargement ───────────── */
  // GET générique : en cas d'erreur, la valeur « vide » est appliquée
  const load = useCallback(async (url, set, { params, pick = (d) => d, empty = [], busy } = {}) => {
    try {
      busy?.(true);
      const { data } = await http.get(url, { params });
      set(pick(data) ?? empty);
    } catch (error) {
      console.error(`Erreur chargement ${url}:`, error);
      set(empty);
    } finally { busy?.(false); }
  }, []);

  const fetchPersonnel = async () => {
    try {
      setLoading(true);
      const { data } = await http.get('/api/personnel/me');
      setPersonnel(data);
    } catch (error) {
      console.error('Erreur récupération personnel:', error);
      setPersonnel(null);
    } finally { setLoading(false); }
  };

  const fetchPlanning = async () => {
    try {
      const filialeId = user?.filiale_id;
      if (!filialeId) return;
      const [p, j] = await Promise.all([http.get(`/api/planning-configuration/${filialeId}`), http.get(`/api/jours-feries/${filialeId}`)]);
      setPlanning(p.data || []); setJoursFeries(j.data || []);
    } catch (error) { console.error('Erreur récupération planning:', error); }
  };

  const fetchEquipe = async () => {
    try {
      const { data } = await http.get('/api/espace-employe/equipe');
      setEquipe(data?.equipe || []); setManager(data?.manager || null);
    } catch (error) { console.error('Erreur récupération équipe:', error); }
  };

  const fetchAbsences = useCallback(() => {
    const params = {};
    if (absenceStatutFilter) params.statut = absenceStatutFilter;
    if (absenceTypeFilter) params.type_absence_id = absenceTypeFilter;
    return load('/api/absences/mes-absences', setAbsences, { params });
  }, [absenceStatutFilter, absenceTypeFilter, load]);
  const fetchMesConges = (id) => load('/api/conges', setMesConges, { params: { personnel_id: id }, pick: (d) => d?.data || d, busy: setLoadingMesConges });
  const fetchPendingConges = useCallback(() => load('/api/conges/manager/pending', setPendingConges, { busy: setLoadingPending }), [load]);
  const fetchCarnet = () => load('/api/carnet-adresse', setCarnetContacts, { busy: setLoadingCarnet });
  const fetchMesDemandesMobilite = () => {
    if (!personnel?.idpersonnel) return undefined;
    return load('/api/demandes-mobilite', setMesDemandesMobilite, { params: { personnel_id: personnel.idpersonnel }, pick: (d) => (Array.isArray(d) ? d : []) });
  };
  const fetchFilialesEtTitres = async () => {
    await load('/api/filiales', setFiliales, { params: { all: true }, pick: (d) => (Array.isArray(d) ? d : []) }); // all=true : toutes les filiales (mobilité)
    if (user?.filiale_id) await load('/api/titres', setTitres, { params: { filiale_id: user.filiale_id }, pick: (d) => (Array.isArray(d) ? d : []) });
  };
  const fetchTitresByFiliale = (filialeId) => (filialeId
    ? load('/api/titres', setTitres, { params: { filiale_id: filialeId, all: true }, pick: (d) => (Array.isArray(d) ? d : []) })
    : setTitres([]));

  /* ───────────── Effets ───────────── */
  useEffect(() => { fetchPersonnel(); load('/api/conges/types', setTypesConge); }, [user, load]);

  useEffect(() => {
    if (!personnel || personnel.is_user_only) return;
    setProfilForm({ telephone: personnel.numtel || '', adresse: personnel.adresse_complete || '' });
    setAccountForm({ username: user?.username || '', email: user?.email || '' });
    load('/api/conges/solde', setConges, { empty: null });
    fetchPlanning();
    load('/api/taches', setTaches);
    load('/api/documents', setDocuments);
    load('/api/personnel-sanctions', setSanctions, { params: { personnel_id: personnel.idpersonnel }, pick: (d) => d?.data || d });
    fetchAbsences();
    load('/api/type-certificat', setTypeCertificatList);
    load('/api/type-absence', setTypeAbsenceList);
    load('/api/formations', setFormations);
    load('/api/notes-frais', setFrais);
    fetchEquipe();
    load('/api/actualites', setActualites);
    load('/api/objectifs', setObjectifs);
    load('/api/conges/titres', setTitresConge, { busy: setLoadingTitres });
    fetchMesConges(personnel.idpersonnel);
    load('/api/bulletins/mes-bulletins', setBulletins, { busy: setLoadingBulletins });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personnel, user]);

  useEffect(() => { if (isManager) fetchPendingConges(); }, [isManager, fetchPendingConges]);

  useEffect(() => {
    if (activeTab === 'carnet') fetchCarnet();
    if (activeTab === 'carriere') { fetchMesDemandesMobilite(); fetchFilialesEtTitres(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, personnel]);

  useEffect(() => { if (personnel) fetchAbsences(); }, [personnel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Le type de congé par défaut doit exister dans la liste reçue
  useEffect(() => {
    if (typesConge.length && !typesConge.some((t) => t.idtype_conge === congeForm.type_conge_id)) {
      setCongeForm((f) => ({ ...f, type_conge_id: typesConge[0].idtype_conge }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typesConge]);

  // Recherche d'employés (anti-rebond : une requête 300 ms après la dernière frappe)
  useEffect(() => {
    const q = rechercheQuery.trim();
    if (q.length < 2) { setRechercheResultats([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        setLoadingRecherche(true);
        const { data } = await http.get('/api/carnet-adresse/recherche', { params: { q } });
        setRechercheResultats(data || []);
      } catch (error) { console.error('Erreur recherche employés:', error); }
      finally { setLoadingRecherche(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [rechercheQuery]);

  /* ───────────── Actions ───────────── */
  const ajouterContact = async (contactId) => {
    try {
      await http.post('/api/carnet-adresse', { contact_id: contactId });
      notify('success', 'Contact ajouté au carnet.');
      fetchCarnet(); setRechercheQuery(''); setRechercheResultats([]);
    } catch (error) { console.error('Erreur ajout contact:', error); notify('error', "L'ajout du contact a échoué."); }
  };
  const retirerContact = async (id) => {
    if (!window.confirm('Retirer ce contact de votre carnet ?')) return;
    try { await http.delete(`/api/carnet-adresse/${id}`); notify('success', 'Contact retiré du carnet.'); fetchCarnet(); }
    catch (error) { console.error('Erreur suppression contact:', error); notify('error', 'La suppression du contact a échoué.'); }
  };

  const handleSubmitMobilite = async (e) => {
    e.preventDefault();
    try {
      setSubmittingMobilite(true);
      await http.post('/api/demandes-mobilite', {
        personnel_id: personnel.idpersonnel, filiale_source_id: user.filiale_id,
        filiale_destination_id: mobiliteForm.filiale_destination_id, titre_destination_id: mobiliteForm.titre_destination_id,
        motivation: mobiliteForm.motivation, disponibilite: mobiliteForm.disponibilite,
      });
      setShowMobiliteModal(false); setMobiliteForm(EMPTY_MOB);
      fetchMesDemandesMobilite();
      notify('success', 'Demande de mobilité envoyée.');
    } catch (error) { console.error('Erreur demande mobilité:', error); notify('error', "L'envoi de la demande a échoué."); }
    finally { setSubmittingMobilite(false); }
  };

  const handleDownloadBulletin = async (b) => {
    try {
      setDownloadingId(b.id);
      await downloadBlob('/api/bulletins/pdf', `bulletin-paie-${b.periode_libelle || b.id}.pdf`,
        { params: { paie_id: b.paie_id, personnel_id: personnel.idpersonnel, is_interim: b.type_paie === 'interim' } });
    } catch (error) {
      console.error('Erreur téléchargement bulletin:', error);
      notify('error', "Le bulletin n'a pas pu être téléchargé. Contactez les RH si le problème persiste.");
    } finally { setDownloadingId(null); }
  };
  const handleDownloadPDF = async (titreId, numero) => {
    try { await downloadBlob(`/api/conges/titres/${titreId}/pdf`, `titre-conge-${numero}.pdf`); }
    catch (error) { console.error('Erreur téléchargement PDF:', error); notify('error', "Le titre n'a pas pu être téléchargé. Contactez les RH si le problème persiste."); }
  };
  const handleDownloadSanction = async (id) => {
    try { await downloadBlob(`/api/personnel-sanctions/${id}/pdf`, `fiche-sanction-${id}.pdf`); }
    catch (error) { console.error('Erreur téléchargement PDF:', error); notify('error', 'Erreur lors du téléchargement du PDF.'); }
  };

  const handleSaveProfil = async (e) => {
    e.preventDefault();
    try {
      setSavingProfil(true);
      await http.put('/api/personnel/me/profil', profilForm);
      await fetchPersonnel(); // rafraîchit l'en-tête
      setProfilEditing(false);
      notify('success', 'Vos informations ont été mises à jour.');
    } catch (error) { console.error('Erreur mise à jour profil:', error); notify('error', 'La mise à jour a échoué. Réessayez ou contactez les RH.'); }
    finally { setSavingProfil(false); }
  };
  const handleSaveAccount = async (e) => {
    e.preventDefault();
    try {
      setSavingAccount(true);
      const response = await http.put('/api/personnel/me/profil', accountForm);
      if (response.data.requireReconnect) { // identifiant ou e-mail modifié : reconnexion
        notify('success', 'Vos identifiants ont été modifiés. Veuillez vous reconnecter.');
        setTimeout(() => logout(), 2000);
      } else { setAccountEditing(false); notify('success', 'Vos informations ont été mises à jour.'); }
    } catch (error) { console.error('Erreur mise à jour compte:', error); notify('error', 'La mise à jour a échoué. Réessayez.'); }
    finally { setSavingAccount(false); }
  };
  const handleChangePwd = async (e) => {
    e.preventDefault();
    if (pwdForm.nouveau !== pwdForm.confirmer) return notify('error', 'Le nouveau mot de passe et sa confirmation ne correspondent pas.');
    if (pwdForm.nouveau.length < 8) return notify('error', 'Le nouveau mot de passe doit contenir au moins 8 caractères.');
    try {
      setSavingPwd(true);
      await http.put('/api/auth/change-password', { ancien_mot_de_passe: pwdForm.ancien, nouveau_mot_de_passe: pwdForm.nouveau });
      setPwdForm({ ancien: '', nouveau: '', confirmer: '' });
      notify('success', 'Mot de passe modifié avec succès.');
    } catch (error) {
      console.error('Erreur changement mot de passe:', error);
      notify('error', error?.response?.data?.message || 'La modification a échoué. Vérifiez votre mot de passe actuel.');
    } finally { setSavingPwd(false); }
  };

  const handleValidation = async (congeId, action) => {
    const ok = action === 'prevalider' ? 'Demande pré-validée.' : 'Demande rejetée.';
    try { await http.put(`/api/conges/${congeId}/${action}`); fetchPendingConges(); notify('success', ok); }
    catch (error) { console.error(`Erreur ${action}:`, error); notify('error', action === 'prevalider' ? 'La pré-validation a échoué.' : 'Le rejet a échoué.'); }
  };

  const joursDemandes = useMemo(() => {
    const { date_debut: debut, date_fin: fin } = congeForm;
    if (!debut || !fin) return 0;
    const d = new Date(debut), f = new Date(fin);
    if (isNaN(d.getTime()) || isNaN(f.getTime()) || f < d) return 0;
    return Math.ceil((f - d) / 86400000) + 1;
  }, [congeForm]);

  const handleSubmitConge = async (e) => {
    e.preventDefault();
    if (joursDemandes <= 0) return notify('error', 'La date de fin doit être postérieure à la date de début.');
    try {
      setSubmittingConge(true);
      await http.post('/api/conges', {
        personnel_id: personnel.idpersonnel, date_debut: congeForm.date_debut, date_fin: congeForm.date_fin,
        nbre_jours: joursDemandes, type_conge_id: congeForm.type_conge_id, motif: congeForm.motif, statut: 'en_attente',
      });
      setShowCongeModal(false);
      setCongeForm((f) => ({ ...f, date_debut: '', date_fin: '', motif: '' }));
      load('/api/conges/solde', setConges, { empty: null });
      fetchMesConges(personnel.idpersonnel);
      notify('success', 'Demande envoyée. Votre responsable la recevra pour validation.');
    } catch (error) { console.error('Erreur soumission congé:', error); notify('error', "La demande n'a pas pu être envoyée."); }
    finally { setSubmittingConge(false); }
  };
  const handleAnnulerConge = async (congeId) => {
    if (!window.confirm('Annuler cette demande de congé ?')) return;
    try {
      await http.delete(`/api/conges/${congeId}`);
      fetchMesConges(personnel.idpersonnel); load('/api/conges/solde', setConges, { empty: null });
      notify('success', 'Demande annulée.');
    } catch (error) { console.error('Erreur annulation congé:', error); notify('error', "L'annulation a échoué."); }
  };

  const closeJustifier = () => { setShowJustifierModal(false); setSelectedAbsence(null); setCertificatForm(EMPTY_CERT); setCertificatFile(null); };
  const handleJustifier = async (e) => {
    e.preventDefault();
    try {
      setSubmittingCert(true);
      const fd = new FormData();
      Object.entries(certificatForm).forEach(([k, v]) => fd.append(k, v));
      if (certificatFile) fd.append('certificat_scanne', certificatFile);
      await http.post(`/api/absences/${selectedAbsence.idabsence}/justifier`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      closeJustifier(); fetchAbsences();
      notify('success', 'Certificat envoyé. En attente de validation RH.');
    } catch (error) { console.error('Erreur justification:', error); notify('error', "Erreur lors de l'envoi du certificat."); }
    finally { setSubmittingCert(false); }
  };

  /* ───────────── Valeurs dérivées ───────────── */
  const weekDates = useMemo(() => {
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
    return ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map((name, i) => {
      const date = new Date(monday); date.setDate(monday.getDate() + i);
      return { name, date, day: date.getDate(), isToday: date.toDateString() === today.toDateString() };
    });
  }, []);

  const tenure = useMemo(() => {
    const d = personnel?.date_entree ? new Date(personnel.date_entree) : null;
    if (!d || isNaN(d.getTime())) return null;
    const now = new Date();
    let m = (now.getFullYear() - d.getFullYear()) * 12 + now.getMonth() - d.getMonth();
    if (now.getDate() < d.getDate()) m -= 1;
    if (m < 0) return null;
    if (m === 0) return "Bienvenue dans l'équipe !";
    const y = Math.floor(m / 12), mo = m % 12;
    const parts = [y > 0 && `${y} an${y > 1 ? 's' : ''}`, mo > 0 && `${mo} mois`].filter(Boolean);
    return `Avec nous depuis ${parts.join(' et ')}`;
  }, [personnel]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Bonjour' : hour < 18 ? 'Bon après-midi' : 'Bonsoir';

  const soldeTotal = conges?.total ?? personnel?.solde_conge ?? 0;
  const cp = conges?.details?.cp;
  const cpTotal = cp?.total || 25;
  const soldePct = Math.min(100, (soldeTotal / cpTotal) * 100);
  const tachesOuvertes = taches.filter((t) => !t.completed);
  const aJustifier = absences.filter((a) => a.statut === 'en_attente_justification');
  const congesEnAttente = mesConges.filter((c) => c.etat === 0);

  /* ───────────── Écrans d'attente ───────────── */
  if (loading) {
    return (<div className="flex items-center justify-center ee"><style>{CSS}</style><Loader label="Préparation de votre espace…" /></div>);
  }
  if (!personnel || personnel.is_user_only) {
    return (
      <div className="flex items-center justify-center p-6 ee">
        <style>{CSS}</style>
        <div className="w-full max-w-md p-8 text-center ee-card">
          <div className="flex items-center justify-center mx-auto mb-3 w-11 h-11 rounded-2xl" style={{ background: TONES.amber[0] }}><AlertCircle className="w-5 h-5" style={{ color: TONES.amber[1] }} /></div>
          <h1 className="text-lg font-extrabold ee-display">Dossier personnel introuvable</h1>
          <p className="mt-2 text-sm ee-muted">Votre compte n'est rattaché à aucun dossier employé. Contactez les ressources humaines pour activer votre espace.</p>
        </div>
      </div>
    );
  }

  // Paie : années disponibles et bulletins de l'année choisie
  const anneeDe = (b) => b.annee || (b.periode_libelle ? b.periode_libelle.slice(-4) : '');
  const annees = [...new Set(bulletins.map(anneeDe).filter(Boolean))].sort((a, b) => b - a);
  const anneeActive = paieFiltre || annees[0] || '';
  const bulletinsAnnee = anneeActive ? bulletins.filter((b) => String(anneeDe(b)) === String(anneeActive)) : bulletins;
  const dernierBulletin = bulletins[0] || null;

  // Carnet : filtre et pagination (champs absents tolérés)
  const q = filtreContacts.trim().toLowerCase();
  const contactsFiltres = carnetContacts.filter((c) => !q || [c.nom, c.prenom, c.email].some((v) => (v || '').toLowerCase().includes(q)));
  const totalPages = Math.max(1, Math.ceil(contactsFiltres.length / contactsPerPage));
  const page = Math.min(contactsPage, totalPages);

  const badges = { conges: congesEnAttente.length, absences: aJustifier.length };
  const todo = [
    aJustifier.length > 0 && { tone: 'amber', icon: AlertCircle, text: `${aJustifier.length} absence${aJustifier.length > 1 ? 's' : ''} à justifier`, cta: 'Justifier', go: 'absences' },
    congesEnAttente.length > 0 && { tone: 'blue', icon: Clock, text: `${congesEnAttente.length} demande${congesEnAttente.length > 1 ? 's' : ''} de congé en attente de validation`, cta: 'Suivre', go: 'conges' },
    isManager && pendingConges.length > 0 && { tone: 'violet', icon: CheckCircle2, text: `${pendingConges.length} demande${pendingConges.length > 1 ? 's' : ''} de votre équipe à valider`, cta: null },
  ].filter(Boolean);

  const Amount = ({ value, devise, className = '' }) => <span className={cx('tabular-nums', className)}>{showAmounts ? money(value, devise) : MASK}</span>;
  const AmountToggle = (props) => (
    <Btn tone="ghost" size="sm" icon={showAmounts ? EyeOff : Eye} onClick={() => setShowAmounts((s) => !s)} aria-pressed={showAmounts} {...props}>
      {showAmounts ? 'Masquer les montants' : 'Afficher les montants'}
    </Btn>
  );

  /* ═════════════════════════ Rendu ═════════════════════════ */
  return (
    <div className="ee">
      <style>{CSS}</style>

      {notice && (
        <div role="status" className="fixed z-[60] max-w-sm right-4 top-4 ee-in">
          <div className="flex items-start gap-2.5 px-4 py-3 text-sm bg-white shadow-xl rounded-2xl" style={{ border: `1px solid ${notice.type === 'error' ? '#FFD3CC' : '#BFEDE6'}` }}>
            {notice.type === 'error' ? <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" style={{ color: C.coral }} /> : <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" style={{ color: C.teal }} />}
            <span className="leading-snug">{notice.message}</span>
            <button type="button" onClick={() => setNotice(null)} aria-label="Fermer" className="ml-1 ee-muted"><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {/* ══════════ Bandeau personnel ══════════ */}
      <header className="relative overflow-hidden ee-hero">
        <div className="ee-orb" style={{ right: -90, top: -110, width: 360, height: 360, background: 'rgba(78,168,255,.14)' }} />
        <div className="ee-orb" style={{ left: '40%', bottom: -150, width: 320, height: 320, background: 'rgba(139,124,246,.16)' }} />
        <div className="ee-orb" style={{ left: -60, top: 30, width: 150, height: 150, background: 'rgba(255,201,60,.10)' }} />
        <div className="relative mx-auto w-full max-w-[1680px] px-4 pb-16 pt-8 lg:px-8">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-5">
              <div className="ee-avatar-ring flex-none">
                <Avatar src={personnel.photo_profil || personnel.photo} nom={personnel.nom} prenom={personnel.prenom} size={88} radius={25} />
              </div>
              <div className="min-w-0">
                <p className="text-sm" style={{ color: '#A9B4E6' }}>{greeting},</p>
                <h1 className="text-3xl font-extrabold text-white ee-display lg:text-4xl">{personnel.prenom} {personnel.nom}</h1>
                <div className="flex flex-wrap items-center mt-2.5 gap-x-4 gap-y-1.5 text-[13px]" style={{ color: '#D5DBF7' }}>
                  <span className="inline-flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5" style={{ color: C.y }} />{personnel.fonction || personnel.titre_nom || 'Fonction non renseignée'}</span>
                  <span className="inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" style={{ color: C.y }} />{currentFiliale?.libelle || 'Filiale'}</span>
                  {personnel.matricule && <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" style={{ color: C.y }} />Matricule {personnel.matricule}</span>}
                </div>
                {tenure && <p className="mt-3 text-sm font-bold" style={{ color: C.y }}>{tenure}</p>}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-5">
              <Ring pct={soldePct} size={128}>
                <span className="text-3xl font-extrabold leading-none text-white ee-display tabular-nums">{soldeTotal}</span>
                <span className="mt-0.5 text-[11px]" style={{ color: '#A9B4E6' }}>jours de congé</span>
              </Ring>
              <div className="grid gap-2">
                <Btn tone="yellow" icon={Plus} onClick={() => setShowCongeModal(true)}>Demander un congé</Btn>
                <Btn tone="glass" icon={Banknote} onClick={() => setActiveTab('paie')}>Mes bulletins</Btn>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-8 sm:grid-cols-4">
            {[
              { l: 'Tâches en cours', v: tachesOuvertes.length, i: CheckCircle2, c: C.teal, go: 'apercu' },
              { l: 'Formations suivies', v: formations.length, i: BookOpen, c: C.sky, go: 'carriere' },
              { l: 'Titres de congé', v: titresConge.length, i: FileText, c: C.y, go: 'conges' },
              { l: 'Bulletins de paie', v: bulletins.length, i: Banknote, c: C.coral, go: 'paie' },
            ].map((s) => (
              <button key={s.l} type="button" onClick={() => setActiveTab(s.go)} className="flex items-center gap-3 px-4 py-3 text-left ee-glass ee-focus">
                <span className="ee-ico" style={{ background: s.c, color: C.navy }}><s.i className="w-4 h-4" /></span>
                <span className="leading-tight">
                  <span className="block text-xl font-extrabold text-white ee-display tabular-nums">{s.v}</span>
                  <span className="block text-[11px]" style={{ color: '#A9B4E6' }}>{s.l}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* ══════════ Onglets flottants ══════════ */}
      <div className="sticky top-0 z-30 mx-auto -mt-7 max-w-[1680px] px-4 lg:px-8">
        <nav className="flex gap-1 p-1.5 overflow-x-auto ee-tabs" aria-label="Sections de mon espace">
          {TABS.filter(tab => {
            // Masquer l'onglet Paie pour les types de contrat STAGE, FORMATION et PRESTATAIRE
            if (tab.id === 'paie' && personnel?.type_contrat_id && CONTRATS_SANS_PAIE_IDS.includes(personnel.type_contrat_id)) {
              return false;
            }
            return true;
          }).map((tab) => (
            <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} aria-current={activeTab === tab.id ? 'page' : undefined} className="ee-tab">
              <tab.icon className="w-4 h-4" />{tab.label}
              {badges[tab.id] > 0 && <span className="ee-badge">{badges[tab.id]}</span>}
            </button>
          ))}
        </nav>
      </div>

      {/* ══════════ Contenu ══════════ */}
      <main className="mx-auto w-full max-w-[1680px] px-4 py-6 lg:px-8">
        <div className="grid grid-cols-12 gap-5">
          <div className="col-span-12 space-y-5 xl:col-span-8 2xl:col-span-9">

            {/* ───────── Aperçu ───────── */}
            {activeTab === 'apercu' && (
              <>
                {todo.length > 0 && (
                  <div className="grid gap-3 md:grid-cols-2 ee-in">
                    {todo.map((t) => (
                      <div key={t.text} className="flex items-center gap-3 px-4 py-3 rounded-2xl" style={{ background: TONES[t.tone][0], color: TONES[t.tone][1] }}>
                        <t.icon className="flex-none w-5 h-5" />
                        <span className="flex-1 text-sm font-bold">{t.text}</span>
                        {t.cta && <button type="button" onClick={() => setActiveTab(t.go)} className="inline-flex items-center gap-1 text-xs font-extrabold ee-focus">{t.cta}<ArrowRight className="w-3.5 h-3.5" /></button>}
                      </div>
                    ))}
                  </div>
                )}

                <Card title="Ma semaine" icon={Clock} accent={C.vio}
                  action={<span className="text-xs ee-muted">Semaine du {weekDates[0].date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}</span>}>
                  <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
                    {weekDates.map((day) => {
                      const cfg = planning.find((p) => p.jour_semaine === (day.date.getDay() || 7));
                      const travail = cfg?.est_jour_travail === 1;
                      const ferie = joursFeries.find((j) => String(j.date).slice(0, 10) === localISO(day.date));
                      const label = ferie ? ferie.nom || 'Jour férié' : travail ? 'Ouvré' : 'Non ouvré';
                      const hours = travail && !ferie && cfg?.heure_debut && cfg?.heure_fin ? `${cfg.heure_debut.substring(0, 5)} – ${cfg.heure_fin.substring(0, 5)}` : null;
                      return (
                        <div key={day.name} className={cx('ee-day', day.isToday ? 'ee-day-today' : ferie ? 'ee-day-ferie' : travail ? 'ee-day-on' : 'ee-day-off')}>
                          <span className="text-[11px] font-bold uppercase tracking-wide opacity-70">{day.name}</span>
                          <span className="text-2xl font-extrabold leading-none ee-display">{day.day}</span>
                          <span className="text-[11px] font-bold leading-tight">{day.isToday ? "Aujourd'hui" : label}</span>
                          {hours && <span className="text-[10px] opacity-70">{hours}</span>}
                        </div>
                      );
                    })}
                  </div>
                </Card>

                <div className="grid gap-5 lg:grid-cols-2">
                  <Card title="Mes tâches" icon={CheckCircle2} accent={C.teal} delay={60}
                    action={<span className="text-xs font-bold ee-muted">{taches.length - tachesOuvertes.length}/{taches.length}</span>}>
                    {taches.length === 0 ? (
                      <Empty icon={CheckCircle2} title="Aucune tâche assignée" hint="Les tâches confiées par votre responsable apparaîtront ici." />
                    ) : (
                      <>
                        <div className="h-1.5 mb-3 overflow-hidden rounded-full" style={{ background: C.bg }}>
                          <div className="h-full rounded-full" style={{ width: `${((taches.length - tachesOuvertes.length) / taches.length) * 100}%`, background: C.teal, transition: 'width .6s' }} />
                        </div>
                        <ul className="grid gap-1">
                          {taches.slice(0, 6).map((t) => (
                            <li key={t.id} className="flex items-center gap-3 px-2 py-2 rounded-xl ee-row">
                              <input type="checkbox" className="ee-check ee-focus" checked={Boolean(t.completed)} aria-label={t.titre}
                                onChange={() => setTaches((l) => l.map((x) => (x.id === t.id ? { ...x, completed: !x.completed } : x)))} />
                              <span className={cx('flex-1 text-sm', t.completed && 'line-through')} style={{ color: t.completed ? C.mut : C.navy }}>{t.titre}</span>
                              {t.echeance && <span className="text-[11px] ee-muted">{fmtDate(t.echeance)}</span>}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </Card>

                  <Card title="Objectifs de l'année" icon={Target} accent={C.coral} delay={120}>
                    {objectifs.length === 0 ? (
                      <Empty icon={Target} title="Aucun objectif défini" hint="Vos objectifs annuels seront visibles après l'entretien d'évaluation." />
                    ) : (
                      <div className="space-y-4">{objectifs.slice(0, 4).map((o) => <GoalBar key={o.id} label={o.titre} value={o.progression} />)}</div>
                    )}
                  </Card>
                </div>

                <div className="grid gap-5 lg:grid-cols-5">
                  <Card title="Dernier bulletin" icon={Banknote} accent={C.y} className="lg:col-span-2" delay={180}>
                    {!dernierBulletin ? (
                      <Empty icon={Banknote} title="Aucun bulletin disponible" hint="Vos bulletins seront publiés ici chaque mois." />
                    ) : (
                      <div>
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-lg font-extrabold ee-display">{dernierBulletin.periode_libelle}</p>
                          <Chip tone={dernierBulletin.type_paie === 'interim' ? 'violet' : 'blue'}>{dernierBulletin.type_paie === 'interim' ? 'Intérim' : 'Normal'}</Chip>
                        </div>
                        <p className="mt-4 text-[11px] font-bold uppercase ee-muted">Net à payer</p>
                        <Amount value={dernierBulletin.net_a_payer} devise={dernierBulletin.devise} className="block text-3xl font-extrabold ee-display" />
                        <p className="mt-1 text-xs ee-muted">Reçu le {fmtDate(dernierBulletin.date_envoi)}</p>
                        <div className="flex flex-wrap gap-2 mt-4">
                          <Btn size="sm" icon={Download} onClick={() => handleDownloadBulletin(dernierBulletin)} disabled={downloadingId === dernierBulletin.id}>Télécharger</Btn>
                          <AmountToggle />
                        </div>
                      </div>
                    )}
                  </Card>

                  <Card title="Mes demandes de congé" icon={CalendarDays} accent={C.sky} flush className="lg:col-span-3" delay={240}
                    action={<button type="button" onClick={() => setActiveTab('conges')} className="inline-flex items-center gap-1 text-xs font-extrabold ee-focus" style={{ color: C.navy2 }}>Tout voir <ChevronRight className="w-3.5 h-3.5" /></button>}>
                    {loadingMesConges ? <Loader /> : mesConges.length === 0 ? (
                      <Empty icon={CalendarDays} title="Aucune demande enregistrée" hint="Posez votre première demande depuis l'onglet Congés." />
                    ) : (
                      <ul className="pb-2">
                        {mesConges.slice(0, 4).map((c) => (
                          <li key={c.idplanning_conge} className="flex items-center gap-3 px-5 py-2.5 ee-row">
                            <DateBlock value={c.date_debut} tone={c.etat === 3 ? C.coral : c.etat === 1 ? C.teal : C.y} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold truncate">{c.type_conge_libelle || 'Congé'}</p>
                              <p className="text-xs ee-muted">Du {fmtDate(c.date_debut)} au {fmtDate(c.date_fin)} · {c.nombre_jour} j</p>
                            </div>
                            <CongeChip etat={c.etat} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </Card>
                </div>
              </>
            )}

            {/* ───────── Congés ───────── */}
            {activeTab === 'conges' && (
              <>
                <div className="grid gap-5 lg:grid-cols-3">
                  <Card title="Solde disponible" icon={Calendar} accent={C.teal}>
                    <div className="flex flex-col items-center">
                      <Ring pct={soldePct} size={150} stroke={12} color={C.teal} track={C.bg}>
                        <span className="text-4xl font-extrabold leading-none ee-display tabular-nums">{soldeTotal}</span>
                        <span className="mt-1 text-xs ee-muted">jours</span>
                      </Ring>
                      <p className="mt-3 text-xs ee-muted">Droits arrêtés au {new Date().toLocaleDateString('fr-FR')}</p>
                    </div>
                    {cp && (
                      <div className="mt-4"><GoalBar label="Congés payés consommés" value={((cp.utilises || 0) / cpTotal) * 100} tone={C.sky} />
                        <p className="mt-1 text-xs text-right ee-muted">{cp.utilises || 0} / {cpTotal} jours</p></div>
                    )}
                    <Btn tone="navy" icon={Plus} className="w-full mt-5" onClick={() => setShowCongeModal(true)}>Demander un congé</Btn>
                  </Card>

                  <Card title="Mes titres de congé" icon={FileText} accent={C.y} className="lg:col-span-2" delay={60}>
                    {loadingTitres ? <Loader /> : titresConge.length === 0 ? (
                      <Empty icon={FileText} title="Aucun titre disponible" hint="Un titre est édité automatiquement dès qu'un congé est validé par les RH." />
                    ) : (
                      <ul className="grid gap-3">
                        {titresConge.map((t) => (
                          <li key={t.idtitre_conge} className={cx('ee-ticket', t.statut === 'annule' && 'is-off')}>
                            <div className="flex-1 min-w-0 p-4">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-sm font-bold">{t.numero_titre}</span>
                                <Chip tone={t.statut === 'annule' ? 'rose' : 'green'}>{t.statut === 'annule' ? 'Annulé' : 'Valide'}</Chip>
                              </div>
                              <p className="mt-1 text-xs truncate ee-muted">{t.type_conge} · du {fmtDate(t.date_debut)} au {fmtDate(t.date_fin)} · {t.nombre_jours} jour(s)</p>
                              <p className="text-[11px] ee-muted">Édité le {fmtDate(t.date_generation)}</p>
                            </div>
                            <div className="ee-cut" />
                            <button type="button" onClick={() => handleDownloadPDF(t.idtitre_conge, t.numero_titre)} aria-label={`Télécharger le titre ${t.numero_titre}`}
                              className="flex flex-col items-center justify-center gap-1 px-5 text-[11px] font-extrabold ee-focus" style={{ color: C.navy2 }}>
                              <Download className="w-5 h-5" />PDF
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Card>
                </div>

                <Card title="Historique de mes demandes" icon={CalendarDays} accent={C.sky} flush delay={120}>
                  {loadingMesConges ? <Loader /> : mesConges.length === 0 ? (
                    <Empty icon={CalendarDays} title="Aucune demande enregistrée" hint="Vos demandes et leur suivi de validation s'afficheront ici."
                      action={<Btn size="sm" icon={Plus} onClick={() => setShowCongeModal(true)} className="mt-2">Demander un congé</Btn>} />
                  ) : (
                    <ul className="pb-2">
                      {mesConges.map((c) => (
                        <li key={c.idplanning_conge} className="flex flex-wrap items-center gap-4 px-5 py-3 ee-row">
                          <DateBlock value={c.date_debut} tone={c.etat === 3 ? C.coral : c.etat === 1 ? C.teal : C.y} />
                          <div className="flex-1 min-w-[200px]">
                            <p className="text-sm font-bold">{c.type_conge_libelle || 'Congé'}</p>
                            <p className="text-xs ee-muted">{fmtDate(c.date_debut)} → {fmtDate(c.date_fin)} · {c.nombre_jour} j{c.date_reprise ? ` · reprise le ${fmtDate(c.date_reprise)}` : ''}</p>
                            <p className="text-[11px] ee-muted">Demandé le {fmtDate(c.date_demande)}</p>
                            {c.motif_rejet && <p className="mt-1 text-xs font-bold" style={{ color: TONES.rose[1] }}>Motif du rejet : {c.motif_rejet}</p>}
                          </div>
                          <CongeChip etat={c.etat} />
                          {c.etat === 0 && <Btn tone="ghost" size="sm" icon={X} onClick={() => handleAnnulerConge(c.idplanning_conge)}>Annuler</Btn>}
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              </>
            )}

            {/* ───────── Absences ───────── */}
            {activeTab === 'absences' && (
              <Card title="Mes absences" icon={Clock} accent={C.coral}
                action={<div className="flex flex-wrap gap-2">
                  <select className="ee-input" style={{ width: 'auto', padding: '6px 10px', fontSize: 12 }} aria-label="Filtrer par statut" value={absenceStatutFilter} onChange={(e) => setAbsenceStatutFilter(e.target.value)}>
                    <option value="">Tous les statuts</option>
                    {Object.entries(ABS).map(([k, v]) => <option key={k} value={k}>{v[1]}</option>)}
                  </select>
                  <select className="ee-input" style={{ width: 'auto', padding: '6px 10px', fontSize: 12 }} aria-label="Filtrer par type" value={absenceTypeFilter} onChange={(e) => setAbsenceTypeFilter(e.target.value)}>
                    <option value="">Tous les types</option>
                    {typeAbsenceList.filter((t) => t.idtype_absence !== 2).map((t) => <option key={t.idtype_absence} value={t.idtype_absence}>{t.libelle}</option>)}
                  </select>
                </div>}>
                {absences.length === 0 ? <Empty icon={Clock} title="Aucune absence" hint="Aucune absence signalée par les RH." /> : (
                  <ul className="grid gap-3">
                    {absences.map((a) => {
                      const s = ABS[a.statut] || ['slate', a.statut || '—', Clock];
                      return (
                        <li key={a.idabsence} className="flex flex-wrap items-start gap-4 p-4 rounded-2xl" style={{ border: `1px solid ${C.line}` }}>
                          <span className="ee-ico" style={{ width: 40, height: 40, background: TONES[s[0]][0], color: TONES[s[0]][1] }}>{s[2] && React.createElement(s[2], { className: "w-5 h-5" })}</span>
                          <div className="flex-1 min-w-[220px]">
                            <p className="font-extrabold ee-display">{a.type_absence_libelle || 'Absence'} <span className="text-sm font-normal ee-muted">({a.nbre_heures} h)</span></p>
                            <p className="text-sm ee-muted">{fmtDate(a.date_debut)} → {fmtDate(a.date_fin)}</p>
                            {a.certificat_id && (
                              <p className="mt-1.5 text-xs ee-muted">
                                {a.libelle_certificat}{a.certificat_numero && ` · N° ${a.certificat_numero}`}{a.date_consultation && ` · consultation le ${fmtDate(a.date_consultation)}`}
                              </p>
                            )}
                            {a.motif_rejet && <p className="px-2.5 py-1.5 mt-2 text-xs rounded-lg" style={{ background: TONES.rose[0], color: TONES.rose[1] }}><b>Motif de rejet :</b> {a.motif_rejet}</p>}
                          </div>
                          {a.statut === 'en_attente_justification'
                            ? <Btn size="sm" icon={Upload} onClick={() => { setSelectedAbsence(a); setShowJustifierModal(true); }}>Justifier</Btn>
                            : <Chip tone={s[0]} icon={s[2]}>{s[1]}</Chip>}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Card>
            )}

            {/* ───────── Paie ───────── */}
            {activeTab === 'paie' && (
              <>
                <section className="relative p-6 overflow-hidden text-white ee-hero ee-in rounded-3xl">
                  <div className="ee-orb" style={{ right: -50, top: -70, width: 220, height: 220, background: 'rgba(255,201,60,.14)' }} />
                  <div className="relative flex flex-wrap items-center justify-between gap-5">
                    <div>
                      <p className="text-sm" style={{ color: '#A9B4E6' }}>Dernier net perçu{dernierBulletin ? ` · ${dernierBulletin.periode_libelle}` : ''}</p>
                      <p className="mt-1 text-4xl font-extrabold ee-display">{dernierBulletin ? <Amount value={dernierBulletin.net_a_payer} devise={dernierBulletin.devise} /> : '—'}</p>
                      <p className="mt-1 text-xs" style={{ color: '#A9B4E6' }}>{dernierBulletin ? `Reçu le ${fmtDate(dernierBulletin.date_envoi)}` : 'Aucun bulletin disponible'}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <Chip tone="blue" icon={FileText}>{bulletins.length} bulletin{bulletins.length > 1 ? 's' : ''}</Chip>
                      <AmountToggle className="!bg-white/10 !text-white !border-white/20" />
                    </div>
                  </div>
                </section>

                <Card title="Mes bulletins de paie" icon={FileText} accent={C.y} delay={60}
                  action={annees.length > 1 && (
                    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Année">
                      {annees.map((a) => (
                        <button key={a} type="button" onClick={() => setPaieFiltre(a)} aria-pressed={String(a) === String(anneeActive)}
                          className="px-3 py-1 text-xs font-extrabold rounded-full ee-focus"
                          style={{ background: String(a) === String(anneeActive) ? C.navy : C.bg, color: String(a) === String(anneeActive) ? '#fff' : C.mut }}>{a}</button>
                      ))}
                    </div>
                  )}>
                  {loadingBulletins ? <Loader label="Chargement des bulletins…" /> : bulletins.length === 0 ? (
                    <Empty icon={Banknote} title="Aucun bulletin disponible" hint="Vos bulletins de paie apparaîtront ici une fois envoyés par le service RH." />
                  ) : (
                    <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                      {bulletinsAnnee.map((b) => (
                        <li key={b.id} className="ee-slip">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="text-lg font-extrabold ee-display">{b.periode_libelle}</p>
                              <p className="text-xs ee-muted">{fmtDate(b.date_debut)} – {fmtDate(b.date_fin)}</p>
                            </div>
                            <Chip tone={b.type_paie === 'interim' ? 'violet' : 'blue'}>{b.type_paie === 'interim' ? 'Intérim' : 'Normal'}</Chip>
                          </div>
                          <p className="mt-4 text-[11px] font-bold uppercase ee-muted">Net à payer</p>
                          <Amount value={b.net_a_payer} devise={b.devise} className="block text-2xl font-extrabold ee-display" />
                          <div className="flex items-center justify-between gap-2 mt-4">
                            <span className="text-xs ee-muted">Reçu le {fmtDate(b.date_envoi)}</span>
                            <Btn size="sm" icon={Download} onClick={() => handleDownloadBulletin(b)} disabled={downloadingId === b.id}>{downloadingId === b.id ? '…' : 'PDF'}</Btn>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              </>
            )}

            {/* ───────── Carrière ───────── */}
            {activeTab === 'carriere' && (
              <>
                <Card title="Demandes de mobilité" icon={Briefcase} accent={C.vio}
                  action={<Btn size="sm" icon={Plus} onClick={() => setShowMobiliteModal(true)}>Nouvelle demande</Btn>}>
                  {mesDemandesMobilite.length === 0 ? (
                    <Empty icon={Briefcase} title="Aucune demande de mobilité" hint="Vous pouvez demander une mobilité inter-filiale ou un changement de poste." />
                  ) : (
                    <ul className="grid gap-2">
                      {mesDemandesMobilite.slice(0, 4).map((d) => {
                        const m = MOB[d.statut] || ['blue', (d.statut || '').replace(/_/g, ' ')];
                        return (
                          <li key={d.id} className="flex items-center gap-3 px-4 py-3 rounded-2xl" style={{ background: C.bg }}>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold truncate">{d.filiale_destination_nom || 'Mobilité'}</p>
                              <p className="text-xs truncate ee-muted">{d.titre_destination_nom_developpe || d.titre_destination_nom || 'Poste'} · {fmtDate(d.date_demande)}</p>
                            </div>
                            <Chip tone={m[0]}>{m[1]}</Chip>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </Card>
                <div className="grid gap-5 lg:grid-cols-2">
                  <Card title="Mes formations" icon={BookOpen} accent={C.sky} delay={60}>
                    {formations.length === 0 ? <Empty icon={BookOpen} title="Aucune formation en cours" hint="Le catalogue de formations est ouvert par votre service RH." /> : (
                      <div className="space-y-4">{formations.map((f) => <GoalBar key={f.id} label={f.titre} value={f.progression} tone={C.vio} />)}</div>
                    )}
                  </Card>
                  <Card title="Objectifs de l'année" icon={Target} accent={C.coral} delay={120}>
                    {objectifs.length === 0 ? <Empty icon={Target} title="Aucun objectif défini" hint="Vos objectifs seront publiés après l'entretien annuel." /> : (
                      <div className="space-y-4">{objectifs.map((o) => <GoalBar key={o.id} label={o.titre} value={o.progression} />)}</div>
                    )}
                  </Card>
                </div>
              </>
            )}

            {/* ───────── Documents ───────── */}
            {activeTab === 'documents' && (
              <div className="grid gap-5 lg:grid-cols-2">
                <Card title="Mes documents" icon={FileText} accent={C.sky} flush>
                  {documents.length === 0 ? <Empty title="Aucun document" hint="Attestations et contrats seront déposés ici par les RH." /> : (
                    <ul className="pb-2">
                      {documents.map((d) => (
                        <li key={d.id} className="flex items-center gap-3 px-5 py-3 ee-row">
                          <span className="ee-ico" style={{ background: C.bg }}><FileText className="w-4 h-4 ee-muted" /></span>
                          <div className="flex-1 min-w-0"><p className="text-sm font-bold truncate">{d.titre}</p><p className="text-xs ee-muted">{fmtDate(d.date_ajout)}</p></div>
                          <ChevronRight className="w-4 h-4 ee-muted" />
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
                <Card title="Mes sanctions" icon={AlertTriangle} accent={C.coral} flush delay={60}>
                  {sanctions.length === 0 ? <Empty icon={Shield} title="Aucune sanction" hint="Aucune sanction disciplinaire enregistrée." /> : (
                    <ul className="pb-2">
                      {sanctions.map((s) => (
                        <li key={s.idpersonnel_sanction} className="flex items-center gap-3 px-5 py-3 ee-row">
                          <span className="ee-ico" style={{ background: TONES.rose[0], color: TONES.rose[1] }}><AlertTriangle className="w-4 h-4" /></span>
                          <div className="flex-1 min-w-0"><p className="text-sm font-bold truncate">{s.libelle_sanction}</p><p className="text-xs ee-muted">{fmtDate(s.date_debut)}</p></div>
                          {s.fiche_pdf && <button type="button" onClick={() => handleDownloadSanction(s.idpersonnel_sanction)} title="Télécharger la fiche" aria-label="Télécharger la fiche" className="p-2 rounded-lg ee-focus" style={{ color: C.navy2 }}><Download className="w-4 h-4" /></button>}
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
                <Card title="Notes de frais" icon={Receipt} accent={C.teal} flush className="lg:col-span-2" delay={120}>
                  {frais.length === 0 ? <Empty icon={Receipt} title="Aucune note de frais" hint="Les remboursements soumis apparaîtront avec leur statut." /> : (
                    <ul className="pb-2">
                      {frais.map((n) => (
                        <li key={n.id} className="flex items-center justify-between gap-4 px-5 py-3 ee-row">
                          <div className="min-w-0"><p className="text-sm font-bold truncate">{n.libelle}</p><p className="text-xs ee-muted">{fmtDate(n.date_soumission)}</p></div>
                          <span className="text-sm font-extrabold tabular-nums">{n.montant}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              </div>
            )}

            {/* ───────── Profil ───────── */}
            {activeTab === 'profil' && (
              <>
                <Card title="Dossier employé" icon={Briefcase} accent={C.sky}>
                  <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
                    <div className="flex flex-col items-center gap-2 shrink-0">
                      <div className="ee-avatar-ring"><Avatar src={personnel.photo_profil || personnel.photo} nom={personnel.nom} prenom={personnel.prenom} size={88} radius={25} /></div>
                      <span className="text-xs ee-muted">{personnel.matricule ? `Matricule ${personnel.matricule}` : '—'}</span>
                    </div>
                    <dl className="grid flex-1 grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                      {[
                        ['Prénom', personnel.prenom], ['Nom', personnel.nom],
                        ['Fonction', personnel.titre_nom || personnel.departement_affectation, true],
                        ['Département', personnel.libelle_departement || personnel.departement_affectation, true],
                        ['Filiale', personnel.filiale_libelle || currentFiliale?.libelle],
                        ["Date d'embauche", fmtDate(personnel.date_entree)],
                        ['Type de contrat', personnel.type_contrat, true],
                        ['Email professionnel', personnel.adressemail || user?.email],
                        ['Nationalité', personnel.nationalite],
                      ].map(([label, value, chip]) => (
                        <div key={label}>
                          <dt className="text-[11px] font-bold uppercase tracking-wide ee-muted">{label}</dt>
                          <dd className="mt-0.5 text-sm">{chip && value ? <Chip tone="blue">{value}</Chip> : value || <span className="ee-muted">—</span>}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                  <p className="flex items-center gap-1.5 mt-5 text-xs ee-muted"><Shield className="w-3.5 h-3.5" />Ces informations sont gérées par le service RH. Pour toute modification, contactez-les directement.</p>
                </Card>

                <Card title="Informations personnelles" icon={UserCog} accent={C.teal} delay={60}
                  action={!profilEditing && <Btn tone="ghost" size="sm" icon={Pencil} onClick={() => setProfilEditing(true)}>Modifier</Btn>}>
                  <form onSubmit={handleSaveProfil}>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field label="Téléphone personnel">
                        {profilEditing ? <input type="tel" className="ee-input" value={profilForm.telephone} placeholder="+225 07 00 00 00 00" onChange={(e) => setProfilForm({ ...profilForm, telephone: e.target.value })} />
                          : <p className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 ee-muted" />{profilForm.telephone || <span className="ee-muted">—</span>}</p>}
                      </Field>
                      <Field label="Adresse complète">
                        {profilEditing ? <input type="text" className="ee-input" value={profilForm.adresse} placeholder="Numéro et nom de rue" onChange={(e) => setProfilForm({ ...profilForm, adresse: e.target.value })} />
                          : <p className="flex items-center gap-2 text-sm"><MapPin className="w-4 h-4 ee-muted" />{profilForm.adresse || <span className="ee-muted">—</span>}</p>}
                      </Field>
                    </div>
                    {profilEditing && (
                      <div className="flex justify-end gap-3 pt-5 mt-6" style={{ borderTop: `1px solid ${C.line}` }}>
                        <Btn tone="ghost" onClick={() => { setProfilEditing(false); setProfilForm({ telephone: personnel.numtel || '', adresse: personnel.adresse_complete || '' }); }}>Annuler</Btn>
                        <button type="submit" disabled={savingProfil} className="ee-btn ee-btn-navy"><Save className="w-4 h-4" />{savingProfil ? 'Enregistrement…' : 'Enregistrer'}</button>
                      </div>
                    )}
                  </form>
                </Card>

                <div className="grid gap-5 lg:grid-cols-2">
                  <Card title="Compte utilisateur" icon={Shield} accent={C.vio} delay={120}
                    action={!accountEditing && <Btn tone="ghost" size="sm" icon={Pencil} onClick={() => setAccountEditing(true)}>Modifier</Btn>}>
                    <form onSubmit={handleSaveAccount} className="space-y-4">
                      <Field label="Identifiant">
                        {accountEditing ? <input type="text" className="ee-input" value={accountForm.username} onChange={(e) => setAccountForm({ ...accountForm, username: e.target.value })} />
                          : <p className="font-mono text-sm">{user?.username || '—'}</p>}
                      </Field>
                      <Field label="Adresse e-mail">
                        {accountEditing ? <input type="email" className="ee-input" value={accountForm.email} onChange={(e) => setAccountForm({ ...accountForm, email: e.target.value })} />
                          : <p className="text-sm">{user?.email || '—'}</p>}
                      </Field>
                      <div><span className="block mb-1.5 text-xs font-bold ee-muted">Rôle</span><p className="text-sm">{user?.libprofil || '—'}</p></div>
                      {accountEditing && (
                        <>
                          <p className="flex items-start gap-2 px-3 py-2.5 text-xs rounded-xl" style={{ background: TONES.amber[0], color: TONES.amber[1] }}>
                            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />Si vous modifiez votre identifiant ou votre adresse e-mail, vous devrez vous reconnecter.
                          </p>
                          <div className="flex justify-end gap-3 pt-4" style={{ borderTop: `1px solid ${C.line}` }}>
                            <Btn tone="ghost" onClick={() => { setAccountEditing(false); setAccountForm({ username: user?.username || '', email: user?.email || '' }); }}>Annuler</Btn>
                            <button type="submit" disabled={savingAccount} className="ee-btn ee-btn-navy"><Save className="w-4 h-4" />{savingAccount ? 'Enregistrement…' : 'Enregistrer'}</button>
                          </div>
                        </>
                      )}
                    </form>
                  </Card>

                  <Card title="Changer le mot de passe" icon={KeyRound} accent={C.coral} delay={180}>
                    <form onSubmit={handleChangePwd} className="space-y-4">
                      {[['ancien', 'Mot de passe actuel', '••••••••'], ['nouveau', 'Nouveau mot de passe', 'Min. 8 caractères'], ['confirmer', 'Confirmer le nouveau', '••••••••']].map(([key, label, ph]) => (
                        <div key={key}>
                          <label htmlFor={`pwd-${key}`} className="block mb-1.5 text-xs font-bold ee-muted">{label}</label>
                          <div className="relative">
                            <input id={`pwd-${key}`} type={showPwd[key] ? 'text' : 'password'} className="ee-input" style={{ paddingRight: 40 }} value={pwdForm[key]} placeholder={ph} required
                              autoComplete={key === 'ancien' ? 'current-password' : 'new-password'} onChange={(e) => setPwdForm({ ...pwdForm, [key]: e.target.value })} />
                            <button type="button" onClick={() => setShowPwd((s) => ({ ...s, [key]: !s[key] }))} aria-label={showPwd[key] ? 'Masquer' : 'Afficher'}
                              className="absolute -translate-y-1/2 right-3 top-1/2 ee-muted">{showPwd[key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                          </div>
                        </div>
                      ))}
                      {pwdForm.nouveau && (() => { const l = pwdLevel(pwdForm.nouveau); return (
                        <div><div className="h-1.5 overflow-hidden rounded-full" style={{ background: C.bg }}><div className="h-full rounded-full" style={{ width: `${l.w}%`, background: l.c, transition: 'width .3s, background .3s' }} /></div>
                          <p className="mt-1 text-[11px] ee-muted">{l.l}</p></div>); })()}
                      <div className="flex justify-end"><button type="submit" disabled={savingPwd} className="ee-btn ee-btn-navy"><KeyRound className="w-4 h-4" />{savingPwd ? 'Enregistrement…' : 'Modifier le mot de passe'}</button></div>
                    </form>
                  </Card>
                </div>

                {formations.length > 0 && (
                  <Card title="Formations & diplômes enregistrés" icon={GraduationCap} accent={C.y} flush delay={240}>
                    <ul className="pb-2">
                      {formations.map((f) => (
                        <li key={f.id} className="flex items-center gap-4 px-5 py-3 ee-row">
                          <span className="ee-ico" style={{ background: C.bg }}><GraduationCap className="w-4 h-4 ee-muted" /></span>
                          <div className="flex-1 min-w-0"><p className="text-sm font-bold truncate">{f.titre}</p>{f.organisme && <p className="text-xs ee-muted">{f.organisme}</p>}</div>
                          {f.date_fin && <span className="text-xs ee-muted">{fmtDate(f.date_fin)}</span>}
                        </li>
                      ))}
                    </ul>
                  </Card>
                )}
              </>
            )}

            {/* ───────── Carnet d'adresses ───────── */}
            {activeTab === 'carnet' && (
              <>
                <Card title="Trouver un collègue" icon={Search} accent={C.sky}>
                  <div className="relative">
                    <Search className="absolute w-4 h-4 -translate-y-1/2 left-3.5 top-1/2 ee-muted" />
                    <input type="text" className="ee-input" style={{ paddingLeft: 40 }} placeholder="Rechercher un employé (nom, prénom, email)…" aria-label="Rechercher un employé"
                      value={rechercheQuery} onChange={(e) => setRechercheQuery(e.target.value)} />
                  </div>
                  {loadingRecherche && <Loader label="Recherche…" />}
                  {rechercheQuery.trim().length >= 2 && !loadingRecherche && rechercheResultats.length === 0 && (
                    <p className="py-6 text-sm text-center ee-muted">Aucun résultat trouvé</p>
                  )}
                  {rechercheResultats.length > 0 && (
                    <ul className="mt-3 overflow-y-auto rounded-2xl" style={{ maxHeight: 280, border: `1px solid ${C.line}` }}>
                      {rechercheResultats.map((e) => (
                        <li key={e.iduser} className="flex items-center justify-between gap-3 px-4 py-3 ee-row" style={{ borderBottom: `1px solid ${C.line}` }}>
                          <div className="flex items-center min-w-0 gap-3">
                            <Avatar src={e.photo_profil} nom={e.nom} prenom={e.prenom} size={40} radius={13} />
                            <div className="min-w-0">
                              <p className="text-sm font-bold truncate">{e.prenom} {e.nom}</p>
                              <p className="flex items-center gap-2 text-xs ee-muted">{e.drapeau && <img src={e.drapeau} alt="" className="object-cover w-5 h-3.5 rounded-sm" />}{e.filiale_libelle || e.filiale_code}{e.numtel && ` · ${e.numtel}`}</p>
                            </div>
                          </div>
                          {!e.est_dans_carnet && <Btn size="sm" icon={Plus} onClick={() => ajouterContact(e.iduser)}>Ajouter</Btn>}
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>

                <Card title="Mes contacts" icon={Users} accent={C.teal} delay={60}
                  action={<span className="text-xs font-bold ee-muted">{carnetContacts.length} contact{carnetContacts.length > 1 ? 's' : ''}</span>}>
                  <div className="relative mb-4">
                    <Search className="absolute w-4 h-4 -translate-y-1/2 left-3.5 top-1/2 ee-muted" />
                    <input type="text" className="ee-input" style={{ paddingLeft: 40 }} placeholder="Filtrer mes contacts…" aria-label="Filtrer mes contacts"
                      value={filtreContacts} onChange={(e) => { setFiltreContacts(e.target.value); setContactsPage(1); }} />
                  </div>
                  {loadingCarnet ? <Loader /> : carnetContacts.length === 0 ? (
                    <Empty icon={Users} title="Votre carnet est vide" hint="Recherchez un collègue ci-dessus pour l'ajouter." />
                  ) : contactsFiltres.length === 0 ? (
                    <p className="py-6 text-sm text-center ee-muted">Aucun contact ne correspond à ce filtre.</p>
                  ) : (
                    <>
                      <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {contactsFiltres.slice((page - 1) * contactsPerPage, page * contactsPerPage).map((c) => (
                          <li key={c.id} className="flex flex-col items-center p-4 text-center ee-slip">
                            <Avatar src={c.photo_profil} nom={c.nom} prenom={c.prenom} size={64} radius={20} />
                            <p className="mt-3 text-sm font-extrabold">{c.prenom} {c.nom}</p>
                            {c.email && <a href={`mailto:${c.email}`} className="max-w-full mt-0.5 text-xs truncate ee-muted hover:underline">{c.email}</a>}
                            {c.numtel && <a href={`tel:${c.numtel}`} className="text-xs ee-muted hover:underline">{c.numtel}</a>}
                            {c.filiale_libelle && <p className="flex items-center gap-1.5 mt-1.5 text-xs ee-muted">{c.drapeau && <img src={c.drapeau} alt="" className="object-cover w-5 h-3.5 rounded-sm" />}{c.filiale_libelle}</p>}
                            <button type="button" onClick={() => retirerContact(c.id)} className="inline-flex items-center gap-1 px-3 py-1.5 mt-3 text-xs font-bold rounded-lg ee-focus" style={{ color: TONES.rose[1] }}>
                              <UserMinus className="w-3.5 h-3.5" />Retirer
                            </button>
                          </li>
                        ))}
                      </ul>
                      {totalPages > 1 && (
                        <div className="flex items-center justify-between pt-4 mt-5" style={{ borderTop: `1px solid ${C.line}` }}>
                          <span className="text-xs ee-muted">Page {page} sur {totalPages}</span>
                          <div className="flex gap-2">
                            <Btn tone="ghost" size="sm" disabled={page === 1} onClick={() => setContactsPage(page - 1)}>Précédent</Btn>
                            <Btn tone="ghost" size="sm" disabled={page >= totalPages} onClick={() => setContactsPage(page + 1)}>Suivant</Btn>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </Card>
              </>
            )}
          </div>

          {/* ══════════ Colonne latérale ══════════ */}
          <aside className="col-span-12 space-y-5 xl:col-span-4 2xl:col-span-3">
            <div className="space-y-5 xl:sticky xl:top-20">

              {isManager && (
                <Card title="Demandes à valider" icon={Clock} accent={C.y} flush
                  action={<div className="flex items-center gap-2">
                    <Chip tone="amber">{pendingConges.length}</Chip>
                    <button type="button" onClick={() => setShowValidation(!showValidation)} aria-expanded={showValidation} aria-label={showValidation ? 'Réduire' : 'Déplier'} className="w-7 h-7 font-bold rounded-lg ee-muted ee-focus">{showValidation ? '−' : '+'}</button>
                  </div>}>
                  {showValidation && (loadingPending ? <Loader /> : pendingConges.length === 0 ? (
                    <Empty icon={CheckCircle2} title="Rien à valider" hint="Les nouvelles demandes de votre équipe arriveront ici." />
                  ) : (
                    <ul className="pb-2">
                      {pendingConges.map((c) => (
                        <li key={c.idplanning_conge} className="flex items-start justify-between gap-3 px-5 py-3 ee-row">
                          <div className="min-w-0">
                            <p className="text-sm font-bold truncate">{c.prenom} {c.nom}</p>
                            <p className="text-xs ee-muted">{c.type_conge_libelle}</p>
                            <p className="mt-0.5 text-xs">{fmtDate(c.date_debut)} → {fmtDate(c.date_fin)} <span className="ee-muted">({c.nombre_jour} j)</span></p>
                          </div>
                          <div className="flex gap-1.5 shrink-0">
                            <button type="button" onClick={() => handleValidation(c.idplanning_conge, 'prevalider')} title="Pré-valider" aria-label={`Pré-valider la demande de ${c.prenom} ${c.nom}`}
                              className="flex items-center justify-center w-9 h-9 rounded-xl ee-focus" style={{ background: C.teal, color: C.navy }}><Check className="w-4 h-4" /></button>
                            <button type="button" onClick={() => handleValidation(c.idplanning_conge, 'rejeter')} title="Rejeter" aria-label={`Rejeter la demande de ${c.prenom} ${c.nom}`}
                              className="flex items-center justify-center w-9 h-9 rounded-xl ee-focus" style={{ background: TONES.rose[0], color: TONES.rose[1] }}><X className="w-4 h-4" /></button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ))}
                </Card>
              )}

              <Card title="Mon équipe" icon={Users} accent={C.vio} delay={60}
                action={equipe.length > 0 && <span className="text-xs font-bold ee-muted">{equipe.length} membre{equipe.length > 1 ? 's' : ''}</span>}>
                {equipe.length === 0 && !manager ? (
                  <Empty icon={Users} title="Équipe non renseignée" hint="Votre rattachement hiérarchique n'est pas encore défini." />
                ) : (
                  <div>
                    {manager && (
                      <div className="flex items-center gap-3 p-3 rounded-2xl" style={{ background: C.bg }}>
                        <Avatar src={manager.photo_profil} nom={manager.nom} prenom={manager.prenom} size={46} radius={15} />
                        <div className="min-w-0"><p className="text-sm font-extrabold truncate">{manager.prenom} {manager.nom}</p><p className="text-xs ee-muted">Responsable hiérarchique</p></div>
                      </div>
                    )}
                    {equipe.length > 0 && (
                      <div className="relative pl-5 mt-3 ml-5" style={{ borderLeft: `2px solid ${C.line}` }}>
                        <ul className="grid gap-2">
                          {equipe.slice(0, 8).map((m) => (
                            <li key={m.id} className="relative flex items-center gap-2.5">
                              <span className="absolute -left-5 top-1/2 w-5 h-0.5" style={{ background: C.line }} aria-hidden="true" />
                              <Avatar src={m.photo_profil} nom={m.nom} prenom={m.prenom} size={34} radius={11} />
                              <span className="text-sm truncate">{m.prenom} {m.nom}</span>
                            </li>
                          ))}
                        </ul>
                        {equipe.length > 8 && <p className="mt-2 text-xs ee-muted">+ {equipe.length - 8} autres collaborateurs</p>}
                      </div>
                    )}
                  </div>
                )}
              </Card>

              <Card title="Actualités" icon={Bell} accent={C.coral} flush delay={120}>
                {actualites.length === 0 ? <Empty icon={Bell} title="Pas d'annonce" hint="Les communications internes s'afficheront ici." /> : (
                  <ul className="pb-2">
                    {actualites.slice(0, 4).map((a, i) => (
                      <li key={a.id} className="flex gap-3 px-5 py-3 ee-row">
                        <span className="flex-none w-1 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />
                        <div className="min-w-0">
                          <p className="text-sm font-bold">{a.titre}</p>
                          <p className="mt-0.5 text-xs leading-relaxed ee-muted" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{a.contenu}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>
          </aside>
        </div>
      </main>

      {/* ══════════ Fenêtres ══════════ */}
      {showCongeModal && (
        <Modal title="Demander un congé" onClose={() => setShowCongeModal(false)} footer={<>
          <Btn tone="ghost" onClick={() => setShowCongeModal(false)}>Annuler</Btn>
          <button type="submit" form="form-conge" disabled={submittingConge || joursDemandes <= 0} className="ee-btn ee-btn-navy">{submittingConge ? 'Envoi…' : 'Envoyer la demande'}</button></>}>
          <form id="form-conge" onSubmit={handleSubmitConge} className="py-1 space-y-4">
            <Field label="Type de congé">
              <select className="ee-input" value={congeForm.type_conge_id} required onChange={(e) => setCongeForm({ ...congeForm, type_conge_id: parseInt(e.target.value, 10) })}>
                {typesConge.map((t) => <option key={t.idtype_conge} value={t.idtype_conge}>{t.Libelle_conge}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Premier jour"><input type="date" className="ee-input" value={congeForm.date_debut} required onChange={(e) => setCongeForm({ ...congeForm, date_debut: e.target.value })} /></Field>
              <Field label="Dernier jour"><input type="date" className="ee-input" value={congeForm.date_fin} min={congeForm.date_debut || undefined} required onChange={(e) => setCongeForm({ ...congeForm, date_fin: e.target.value })} /></Field>
            </div>
            <div className="flex items-center justify-between px-4 py-3 text-sm rounded-xl" style={{ background: C.bg }}>
              <span className="ee-muted">Durée demandée</span>
              <span className="font-extrabold tabular-nums">{joursDemandes > 0 ? `${joursDemandes} jour(s)` : '—'}</span>
            </div>
            {joursDemandes > 0 && (joursDemandes > soldeTotal
              ? <p className="flex items-start gap-2 text-xs" style={{ color: TONES.amber[1] }}><AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />Cette demande dépasse votre solde de {soldeTotal} jour(s). Elle devra être arbitrée par les RH.</p>
              : <p className="text-xs ee-muted">Il vous resterait {soldeTotal - joursDemandes} jour(s) après cette demande.</p>)}
            <Field label="Motif (facultatif)"><textarea rows="3" className="ee-input" value={congeForm.motif} placeholder="Précisez le contexte si nécessaire" onChange={(e) => setCongeForm({ ...congeForm, motif: e.target.value })} /></Field>
          </form>
        </Modal>
      )}

      {showJustifierModal && selectedAbsence && (
        <Modal wide title="Justifier l'absence" onClose={closeJustifier} footer={<>
          <Btn tone="ghost" onClick={closeJustifier}>Annuler</Btn>
          <button type="submit" form="form-justif" disabled={submittingCert} className="ee-btn ee-btn-navy"><Upload className="w-4 h-4" />{submittingCert ? 'Envoi…' : 'Envoyer'}</button></>}>
          <form id="form-justif" onSubmit={handleJustifier} className="py-1 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Type de certificat">
                <select className="ee-input" value={certificatForm.type_certificat_id} required onChange={(e) => setCertificatForm({ ...certificatForm, type_certificat_id: e.target.value })}>
                  <option value="">Sélectionner</option>
                  {typeCertificatList.map((t) => <option key={t.idtype_certificat} value={t.idtype_certificat}>{t.libelle_certificat}</option>)}
                </select>
              </Field>
              <Field label="Numéro du certificat"><input type="text" className="ee-input" value={certificatForm.numero} required onChange={(e) => setCertificatForm({ ...certificatForm, numero: e.target.value })} /></Field>
              <Field label="Date de consultation"><input type="date" className="ee-input" value={certificatForm.date_consultation} required onChange={(e) => setCertificatForm({ ...certificatForm, date_consultation: e.target.value })} /></Field>
              <Field label="Date de début"><input type="date" className="ee-input" value={certificatForm.date_debut} required onChange={(e) => setCertificatForm({ ...certificatForm, date_debut: e.target.value })} /></Field>
              <Field label="Date de fin"><input type="date" className="ee-input" value={certificatForm.date_fin} required onChange={(e) => setCertificatForm({ ...certificatForm, date_fin: e.target.value })} /></Field>
              <Field label="Total jours de repos"><input type="number" min="0" className="ee-input" value={certificatForm.total_jour_repos} required onChange={(e) => setCertificatForm({ ...certificatForm, total_jour_repos: e.target.value })} /></Field>
            </div>
            <Field label="Fichier du certificat (PDF, JPG, PNG)">
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="ee-input" required onChange={(e) => setCertificatFile(e.target.files[0])} />
            </Field>
          </form>
        </Modal>
      )}

      {showMobiliteModal && (
        <Modal title="Demander une mobilité" onClose={() => setShowMobiliteModal(false)} footer={<>
          <Btn tone="ghost" onClick={() => setShowMobiliteModal(false)}>Annuler</Btn>
          <button type="submit" form="form-mob" disabled={submittingMobilite} className="ee-btn ee-btn-navy">{submittingMobilite ? 'Envoi…' : 'Envoyer la demande'}</button></>}>
          <form id="form-mob" onSubmit={handleSubmitMobilite} className="py-1 space-y-4">
            <Field label="Filiale de destination">
              <select className="ee-input" value={mobiliteForm.filiale_destination_id} required
                onChange={(e) => { const id = e.target.value; setMobiliteForm({ ...mobiliteForm, filiale_destination_id: id, titre_destination_id: '' }); fetchTitresByFiliale(id); }}>
                <option value="">Sélectionner</option>
                {filiales.map((f) => <option key={f.id} value={f.id}>{f.libelle}</option>)}
              </select>
            </Field>
            <Field label="Poste souhaité">
              <select className="ee-input" value={mobiliteForm.titre_destination_id} disabled={!mobiliteForm.filiale_destination_id} onChange={(e) => setMobiliteForm({ ...mobiliteForm, titre_destination_id: e.target.value })}>
                <option value="">Sélectionner (optionnel)</option>
                {titres.map((t) => <option key={t.idtitre} value={t.idtitre}>{t.nom_developpe || t.nom}</option>)}
              </select>
              {!mobiliteForm.filiale_destination_id && <span className="block mt-1 text-xs ee-muted">Sélectionnez d'abord une filiale de destination</span>}
            </Field>
            <Field label="Date de disponibilité"><input type="date" className="ee-input" value={mobiliteForm.disponibilite} onChange={(e) => setMobiliteForm({ ...mobiliteForm, disponibilite: e.target.value })} /></Field>
            <Field label="Motivation *">
              <textarea rows="4" className="ee-input" value={mobiliteForm.motivation} required placeholder="Expliquez les raisons de votre demande et vos objectifs professionnels…" onChange={(e) => setMobiliteForm({ ...mobiliteForm, motivation: e.target.value })} />
            </Field>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default EspaceEmploye;